import { useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import debounce from 'lodash/debounce';
import { nullSafeGet } from '../utils/DataAccessUtils';
import { ROLE_TYPES } from '../utils/DataConstants';
import {
	appendWalkthroughNotesForBuyer,
	appendWalkthroughNotesForSupplier,
	appendWalkthroughNotesBulkForBuyer,
	appendWalkthroughNotesBulkForSupplier,
} from '../thunks/walkthroughs_thunks';
import { fetchAllUsersForWalkthroughs } from '../thunks/walkthroughs_subscribers_thunks';
import { walkthroughNoteChatUploadCustomRequest } from '../thunks/file_thunks';
import { formatAndSortUserContacts } from '../utils/DataFormatterUtils';

/**
 * Hook for sending notes to a walkthrough.
 * Selects the right thunk based on user type (buyer/supplier).
 */
export const useWalkthroughNoteSender = (walkthrough: any) => {
	const dispatch = useDispatch();
	const currentUser = useSelector((state: any) => state.session.currentUser);

	const walkthroughId = nullSafeGet('id', walkthrough);

	const sendNote = useCallback(
		(noteEntity: any) => {
			const appendThunk =
				currentUser.userType === ROLE_TYPES.SUPPLIER
					? appendWalkthroughNotesForSupplier
					: appendWalkthroughNotesForBuyer;

			return (dispatch as any)(
				appendThunk({
					id: walkthroughId,
					note: noteEntity,
				})
			);
		},
		[dispatch, currentUser, walkthroughId]
	);

	// Photo-only. Appends the whole batch in one request so the backend raises a single
	// grouped notification instead of one per photo.
	const sendNotesBulk = useCallback(
		(noteEntities: any[]) => {
			const appendThunk =
				currentUser.userType === ROLE_TYPES.SUPPLIER
					? appendWalkthroughNotesBulkForSupplier
					: appendWalkthroughNotesBulkForBuyer;

			return (dispatch as any)(
				appendThunk({
					id: walkthroughId,
					notes: noteEntities,
				})
			);
		},
		[dispatch, currentUser, walkthroughId]
	);

	return { sendNote, sendNotesBulk, currentUser, walkthroughId };
};

/**
 * Hook for debounced @mention user search in walkthrough notes.
 */
export const useWalkthroughMentionSearch = (walkthrough: any) => {
	const dispatch = useDispatch();
	const currentUser = useSelector((state: any) => state.session.currentUser);

	const walkthroughId = nullSafeGet('id', walkthrough);

	const loadUsers = useCallback(
		(key: string, cb: (results: any[]) => void) => {
			const count = key.split(' ').length - 1;
			if (count < 2) {
				(dispatch as any)(
					fetchAllUsersForWalkthroughs(key, walkthroughId)(currentUser.userType)()
				).then((data: any) => cb(formatAndSortUserContacts(data)));
			} else {
				cb([]);
			}
		},
		[currentUser.userType, dispatch, walkthroughId]
	);

	const debouncedLoadUsers = useMemo(() => debounce(loadUsers, 250), [loadUsers]);

	const onSearch = useCallback(
		(search: string, cb: (results: any[]) => void) => {
			debouncedLoadUsers(search, cb);
		},
		[debouncedLoadUsers]
	);

	return { onSearch };
};

/**
 * Returns a file upload function that uses the walkthrough-specific upload endpoint.
 * Returns `cloudFrontUrl` (publicly accessible CDN URL) matching the WO chat upload pattern.
 */
export const useWalkthroughFileUpload = (walkthroughId: string | number) => {
	const currentUser = useSelector((state: any) => state.session.currentUser);

	const uploadFile = useCallback(
		({ file, onProgress, onSuccess, onError, workOrderId }: any) => {
			walkthroughNoteChatUploadCustomRequest({
				userType: currentUser.userType,
				walkthroughId: workOrderId || walkthroughId,
				file,
				onProgress,
				onSuccess,
				onError,
			});
		},
		[currentUser.userType, walkthroughId]
	);

	return { uploadFile };
};
